import { useState, useEffect, useCallback } from 'react';
import {
  HiReceiptRefund,
  HiUser,
  HiMail,
  HiRefresh,
  HiExclamationCircle,
  HiDocumentText,
  HiClock,
  HiChevronDown,
  HiCurrencyDollar,
  HiShoppingBag,
  HiCube,
  HiSearch,
  HiBan,
  HiArchive,
  HiShieldExclamation,
} from 'react-icons/hi';
import Badge from '../components/ui/Badge';
import { useAlert } from '../contexts/AlertContext';
import { isAdmin } from '../utils/auth';

const API_BASE = '/api/admin';

export default function SellerTransactions() {
  const showAlert = useAlert();

  const [cashiers, setCashiers] = useState([]);
  const [loadingCashiers, setLoadingCashiers] = useState(true);
  const [selectedSellerId, setSelectedSellerId] = useState('');

  const [data, setData] = useState(null);
  const [loadingSales, setLoadingSales] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [search, setSearch] = useState('');
  const [expandedSale, setExpandedSale] = useState(null);

  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const fetchCashiers = useCallback(async () => {
    setLoadingCashiers(true);
    try {
      const res = await fetch(`${API_BASE}/users/cashiers`, { headers });
      if (!res.ok) throw new Error(`فشل في تحميل البائعين (${res.status})`);
      setCashiers(await res.json());
    } catch (err) {
      showAlert({ type: 'error', title: 'خطأ', message: err.message });
    } finally {
      setLoadingCashiers(false);
    }
  }, []);

  const fetchSales = useCallback(async (sellerId, showRefresh = false) => {
    if (!sellerId) return;
    if (showRefresh) setRefreshing(true);
    else setLoadingSales(true);
    setExpandedSale(null);

    try {
      const res = await fetch(`${API_BASE}/users/sales`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ userId: sellerId }),
      });
      if (!res.ok) throw new Error(`فشل في تحميل المعاملات (${res.status})`);
      setData(await res.json());
    } catch (err) {
      showAlert({ type: 'error', title: 'خطأ', message: err.message });
    } finally {
      setLoadingSales(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchCashiers(); }, [fetchCashiers]);

  const handleSelectSeller = (id) => {
    setSelectedSellerId(id);
    setSearch('');
    setData(null);
    if (id) fetchSales(id);
  };

  const confirmCancel = async () => {
    if (cancelling || !cancelTarget) return;
    setCancelling(true);
    try {
      const res = await fetch(`${API_BASE}/sales/${cancelTarget.id}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `فشل في إلغاء المعاملة (${res.status})`);
      }
      setCancelTarget(null);
      showAlert({
        type: 'success',
        title: 'تم إلغاء المعاملة',
        message: 'تم حذف الفاتورة من سجل المبيعات وإرجاع المنتجات إلى المخزون بنجاح',
      });
      fetchSales(selectedSellerId, true);
    } catch (err) {
      showAlert({ type: 'error', title: 'خطأ', message: err.message });
    } finally {
      setCancelling(false);
    }
  };

  // Admin-only guard (the backend enforces this too, this is just a friendly UI fallback)
  if (!isAdmin()) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
          <HiShieldExclamation className="w-8 h-8 text-red-500" />
        </div>
        <h2 className="text-lg font-bold text-gray-800 mb-1">صلاحية غير كافية</h2>
        <p className="text-sm text-gray-400">هذه الصفحة مخصصة للمسؤولين فقط</p>
      </div>
    );
  }

  const sales = data?.sales || [];
  const filteredSales = sales.filter((s) =>
    s.invoiceNumber.toLowerCase().includes(search.toLowerCase())
  );

  const selectedSeller = cashiers.find((c) => c.id === selectedSellerId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between animate-fadeIn">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-2xl bg-primary-50">
            <HiReceiptRefund className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">معاملات البائعين</h1>
            <p className="text-sm text-gray-400">عرض معاملات البائع وإلغاء أي عملية بيع مع إرجاع المنتجات للمخزون</p>
          </div>
        </div>
        {selectedSellerId && (
          <button
            onClick={() => fetchSales(selectedSellerId, true)}
            disabled={refreshing || loadingSales}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-50 text-primary-600 text-sm font-semibold hover:bg-primary-100 transition-all active:scale-95 disabled:opacity-50"
          >
            <HiRefresh className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            تحديث
          </button>
        )}
      </div>

      {/* Seller selector */}
      <div className="bg-white rounded-2xl shadow-sm border border-primary-100/40 p-4 animate-fadeIn">
        <label className="block text-sm font-semibold text-gray-700 mb-2">اختر البائع</label>
        {loadingCashiers ? (
          <div className="h-12 bg-gray-100 rounded-xl animate-pulse" />
        ) : cashiers.length > 0 ? (
          <div className="relative">
            <HiUser className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <select
              value={selectedSellerId}
              onChange={(e) => handleSelectSeller(e.target.value)}
              className="w-full pr-10 pl-4 py-3 rounded-xl border border-gray-200 bg-gray-50/50 text-sm font-medium text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all cursor-pointer"
            >
              <option value="">— اختر بائعاً لعرض معاملاته —</option>
              {cashiers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.email})
                </option>
              ))}
            </select>
            <HiChevronDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        ) : (
          <p className="text-sm text-gray-400 py-2">لا يوجد بائعون مسجلون</p>
        )}
      </div>

      {/* Empty state — no seller selected */}
      {!selectedSellerId && !loadingCashiers && (
        <div className="bg-white rounded-2xl shadow-sm border border-primary-100/40 flex flex-col items-center justify-center py-20 text-gray-300 animate-fadeIn">
          <HiReceiptRefund className="w-14 h-14 mb-3" />
          <p className="text-base font-semibold text-gray-400">اختر بائعاً للبدء</p>
          <p className="text-sm text-gray-300 mt-1">سيتم عرض جميع معاملاته مع إمكانية الإلغاء</p>
        </div>
      )}

      {/* Loading sales */}
      {loadingSales && (
        <div className="space-y-4 animate-pulse">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => <div key={i} className="h-28 bg-gray-200 rounded-2xl" />)}
          </div>
          <div className="h-80 bg-gray-200 rounded-2xl" />
        </div>
      )}

      {/* Sales content */}
      {selectedSellerId && data && !loadingSales && (
        <>
          {/* Seller summary card */}
          <div className="bg-gradient-to-l from-primary-500 to-primary-400 rounded-2xl shadow-md p-5 text-white animate-fadeIn flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
              <span className="text-xl font-bold">{(selectedSeller?.name || '؟').charAt(0)}</span>
            </div>
            <div>
              <h3 className="text-lg font-bold">{selectedSeller?.name}</h3>
              <p className="text-xs text-primary-100 flex items-center gap-1 mt-0.5">
                <HiMail className="w-3 h-3" />
                {selectedSeller?.email}
              </p>
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <KPICard title="إجمالي المبيعات" value={data.summary?.totalSales || 0} icon={HiShoppingBag} subtitle="عملية بيع" color="primary" />
            <KPICard title="إجمالي الإيرادات" value={`${(data.summary?.totalRevenue || 0).toFixed(2)} د.ا`} icon={HiCurrencyDollar} subtitle="إيرادات البائع" color="blue" />
            <KPICard title="العناصر المباعة" value={data.summary?.totalItemsSold || 0} icon={HiCube} subtitle="إجمالي الوحدات" color="green" />
          </div>

          {/* Search */}
          {sales.length > 0 && (
            <div className="relative">
              <HiSearch className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="ابحث برقم الفاتورة..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pr-11 pl-4 py-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all shadow-sm"
              />
            </div>
          )}

          {/* Transactions list */}
          <div className="bg-white rounded-2xl shadow-sm border border-primary-100/40 overflow-hidden animate-fadeIn">
            <div className="flex items-center gap-3 p-5 border-b border-gray-100">
              <div className="p-2.5 rounded-xl bg-primary-50">
                <HiDocumentText className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-800">سجل المعاملات</h3>
                <p className="text-xs text-gray-400">
                  {filteredSales.length === sales.length
                    ? `${sales.length} عملية بيع`
                    : `${filteredSales.length} من ${sales.length} عملية بيع`}
                </p>
              </div>
            </div>

            {filteredSales.length > 0 ? (
              <div className="divide-y divide-gray-50">
                {filteredSales.map((sale, idx) => (
                  <div key={sale.id}>
                    <div className="flex items-center gap-4 px-5 py-4 hover:bg-primary-50/30 transition-colors">
                      <span className="text-xs font-bold text-gray-300 w-5 text-center flex-shrink-0">{idx + 1}</span>
                      <button
                        onClick={() => setExpandedSale(expandedSale === sale.id ? null : sale.id)}
                        className="flex items-center gap-4 flex-1 min-w-0 text-right"
                      >
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-400 flex items-center justify-center shadow-sm flex-shrink-0">
                          <HiDocumentText className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-bold text-gray-800">{sale.invoiceNumber}</span>
                            <Badge color="purple">{sale.itemsCount} منتج</Badge>
                            <Badge color="blue">{sale.totalQuantity} عنصر</Badge>
                          </div>
                          <span className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                            <HiClock className="w-3 h-3" />
                            {new Date(sale.saleDate).toLocaleDateString('ar-EG', { year: 'numeric', month: 'short', day: 'numeric' })}
                            {' '}
                            {new Date(sale.saleDate).toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-base font-bold text-emerald-600">{sale.totalAmount.toFixed(2)} د.ا</span>
                          <HiChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${expandedSale === sale.id ? 'rotate-180' : ''}`} />
                        </div>
                      </button>
                      <button
                        onClick={() => setCancelTarget(sale)}
                        title="إلغاء المعاملة وإرجاع المنتجات للمخزون"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 hover:shadow-md hover:shadow-red-500/10 transition-all duration-200 active:scale-95 flex-shrink-0"
                      >
                        <HiBan className="w-4 h-4" />
                        إلغاء
                      </button>
                    </div>

                    {/* Expanded items */}
                    {expandedSale === sale.id && (
                      <div className="bg-gray-50/60 border-t border-gray-100 px-5 py-4 animate-fadeIn">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                <th className="px-3 py-2 w-8">#</th>
                                <th className="px-3 py-2">المنتج</th>
                                <th className="px-3 py-2">الباركود</th>
                                <th className="px-3 py-2">الكمية</th>
                                <th className="px-3 py-2">سعر الوحدة</th>
                                <th className="px-3 py-2">الإجمالي</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {sale.items.map((item, i) => (
                                <tr key={i} className="hover:bg-white/70 transition-colors">
                                  <td className="px-3 py-2.5"><span className="text-xs font-bold text-gray-300">{i + 1}</span></td>
                                  <td className="px-3 py-2.5">
                                    <div className="flex items-center gap-2">
                                      <div className="w-6 h-6 rounded-md bg-primary-100 flex items-center justify-center">
                                        <span className="text-[10px] font-bold text-primary-600">{item.product.name.charAt(0)}</span>
                                      </div>
                                      <span className="text-sm font-semibold text-gray-700">{item.product.name}</span>
                                    </div>
                                  </td>
                                  <td className="px-3 py-2.5">
                                    <span className="text-xs text-gray-400 font-mono bg-white px-1.5 py-0.5 rounded border border-primary-100/40">
                                      {item.product.barcode || '—'}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2.5"><span className="text-sm font-bold text-gray-700">{item.quantity}</span></td>
                                  <td className="px-3 py-2.5"><span className="text-sm text-gray-600">{item.unitPrice.toFixed(2)} د.ا</span></td>
                                  <td className="px-3 py-2.5"><span className="text-sm font-bold text-emerald-600">{item.totalPrice.toFixed(2)} د.ا</span></td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="border-t-2 border-gray-200">
                                <td colSpan="3" className="px-3 py-2.5">
                                  <span className="text-xs text-gray-400">{sale.items.length} منتج &middot; {sale.totalQuantity} عنصر</span>
                                </td>
                                <td colSpan="2" className="px-3 py-2.5 text-left"><span className="text-sm font-bold text-gray-700">الإجمالي</span></td>
                                <td className="px-3 py-2.5"><span className="text-sm font-bold text-primary-600">{sale.totalAmount.toFixed(2)} د.ا</span></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-gray-300">
                <HiDocumentText className="w-12 h-12 mb-3" />
                <p className="text-base font-semibold text-gray-400">
                  {search ? 'لا توجد نتائج للبحث' : 'لا توجد معاملات'}
                </p>
                <p className="text-sm text-gray-300 mt-1">
                  {search ? 'جرب رقم فاتورة آخر' : 'لم يقم هذا البائع بأي عملية بيع بعد'}
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Cancel confirmation */}
      {cancelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !cancelling && setCancelTarget(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md animate-scaleIn overflow-hidden">
            <div className="p-6 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <HiBan className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">تأكيد إلغاء المعاملة</h3>
              <p className="text-sm text-gray-500 mb-1">هل أنت متأكد من إلغاء الفاتورة</p>
              <p className="text-base font-bold text-primary-600 mb-4">"{cancelTarget.invoiceNumber}"؟</p>

              {/* Warning banner */}
              <div className="flex items-start gap-2.5 text-right bg-amber-50 border border-amber-200 rounded-xl p-3.5 mb-4">
                <HiExclamationCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs font-semibold text-amber-700 leading-relaxed">
                  تحذير: هذا إجراء حسّاس. سيتم إلغاء عملية البيع بشكل دائم ولن تتمكن من استرجاعها. يُرجى التأكد قبل المتابعة.
                </p>
              </div>

              {/* Consequence summary */}
              <div className="text-right bg-gray-50 rounded-xl border border-gray-100 p-4 mb-5 space-y-2.5">
                <div className="flex items-center gap-2 text-sm">
                  <HiArchive className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span className="text-gray-600">
                    سيتم إرجاع <span className="font-bold text-gray-800">{cancelTarget.totalQuantity}</span> عنصر إلى المخزون
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <HiCurrencyDollar className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  <span className="text-gray-600">
                    سيتم خصم <span className="font-bold text-gray-800">{cancelTarget.totalAmount.toFixed(2)} د.ا</span> من المبيعات
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <HiDocumentText className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <span className="text-gray-600">سيتم حذف الفاتورة نهائياً من سجل المبيعات</span>
                </div>
              </div>

              <p className="text-xs text-gray-400 mb-5">لا يمكن التراجع عن هذا الإجراء بعد التأكيد</p>

              <div className="flex gap-3">
                <button
                  onClick={() => setCancelTarget(null)}
                  disabled={cancelling}
                  className="flex-1 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold transition-all active:scale-95 disabled:opacity-50"
                >
                  تراجع
                </button>
                <button
                  onClick={confirmCancel}
                  disabled={cancelling}
                  className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-semibold shadow-lg shadow-red-500/25 transition-all active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {cancelling ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      جاري الإلغاء...
                    </>
                  ) : (
                    'نعم، ألغِ المعاملة'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KPICard({ title, value, icon: Icon, subtitle, color = 'primary' }) {
  const colorClasses = {
    primary: { bg: 'bg-primary-50', text: 'text-primary-600', glow: 'shadow-primary-500/10' },
    green: { bg: 'bg-emerald-50', text: 'text-emerald-600', glow: 'shadow-emerald-500/10' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', glow: 'shadow-blue-500/10' },
    orange: { bg: 'bg-orange-50', text: 'text-orange-600', glow: 'shadow-orange-500/10' },
  };
  const c = colorClasses[color];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-primary-100/40 p-5 hover:shadow-md hover:-translate-y-1 transition-all duration-300 animate-fadeIn">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-sm text-gray-500 font-medium">{title}</p>
          <p className="text-2xl md:text-3xl font-bold text-gray-800">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 font-medium mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-xl ${c.bg} ${c.text} shadow-lg ${c.glow}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}
